import { type FC, memo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AgentTable from './AgentTable/AgentTable';
import AgentTableCell from './AgentTable/AgentTableCell';
import styles from './ChatMarkdown.module.css';

const REMARK_PLUGINS = [remarkGfm];
const COMPONENTS: Components = { table: AgentTable, td: AgentTableCell };
const STANDALONE_NUMBERED_BULLET_PATTERN = /^(\s*)[-*]\s+(\d+[.)])\s*$/;
const BULLET_TEXT_PATTERN = /^\s*[-*]\s+(.+)$/;
const BOLD_NUMBERED_HEADING_WITH_BODY_PATTERN = /^(\s*)\*\*(\d+[.)]\s+[^*]+)\*\*\s+(.+)$/;
const INLINE_HEADING_SPLIT_PATTERN = /^(?!\s{0,3}#)(.*?[^\s\w#])(#{1,6}\s+\S.*)$/;

function normalizeStreamedMarkdown(content: string): string {
  const lines = content.split(/\r?\n/);
  const consumedLineIndexes = new Set<number>();

  return lines
    .flatMap((line, lineIndex) => {
      if (consumedLineIndexes.has(lineIndex)) {
        return [];
      }

      const inlineHeadingMatch = INLINE_HEADING_SPLIT_PATTERN.exec(line);

      if (inlineHeadingMatch) {
        return [inlineHeadingMatch[1], '', inlineHeadingMatch[2]];
      }

      const boldHeadingMatch = BOLD_NUMBERED_HEADING_WITH_BODY_PATTERN.exec(line);

      if (boldHeadingMatch) {
        return [`${boldHeadingMatch[1]}**${boldHeadingMatch[2]}**`, '', `${boldHeadingMatch[1]}${boldHeadingMatch[3]}`];
      }

      const numberedBulletMatch = STANDALONE_NUMBERED_BULLET_PATTERN.exec(line);
      const nextBulletMatch = numberedBulletMatch ? BULLET_TEXT_PATTERN.exec(lines[lineIndex + 1] ?? '') : null;

      if (numberedBulletMatch && nextBulletMatch) {
        consumedLineIndexes.add(lineIndex + 1);
        return [`${numberedBulletMatch[1]}${numberedBulletMatch[2]} ${nextBulletMatch[1]}`];
      }

      return [line];
    })
    .join('\n');
}

interface ChatMarkdownProps {
  content: string;
}

const ChatMarkdown: FC<ChatMarkdownProps> = ({ content }) => (
  <div className={styles.markdown}>
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
      {normalizeStreamedMarkdown(content)}
    </ReactMarkdown>
  </div>
);

export default memo(ChatMarkdown);
